import { useState, useCallback, useRef } from "react";
import { message } from "antd";
import { getAllOperations } from "../../operations/services/operationService";
import { getAdminRoles, getAdminExtraPermissions, saveAdminExtraPermissions } from "../services/adminPermissionService";
import { PermissionTransferItem } from "../config/types";

interface UseAdminPermissionManagementReturn {
  adminPermissions: PermissionTransferItem[];
  allPermissions: any[];
  rolePermissions: string[]; // 来自角色的权限ID列表
  extraPermissions: string[]; // 管理员额外权限ID列表
  rolePermissionSources: Record<string, string[]>; // 权限ID -> 角色名称列表
  permissionLoading: boolean;
  saveLoading: boolean;
  fetchAdminPermissionsAndAll: (adminId: string) => Promise<void>;
  handleSaveAdminPermissions: (adminId: string, selectedPermissions?: PermissionTransferItem[]) => Promise<void>;
  updateAdminPermissionCount?: (adminId: string, count: number) => void;
  setAdminPermissions: (permissions: PermissionTransferItem[]) => void;
}

export const useAdminPermissionManagement = (
  updateAdminPermissionCount?: (adminId: string, count: number) => void
): UseAdminPermissionManagementReturn => {
  const [adminPermissions, setAdminPermissions] = useState<PermissionTransferItem[]>([]);
  const [allPermissions, setAllPermissions] = useState<any[]>([]);
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);
  const [extraPermissions, setExtraPermissions] = useState<string[]>([]);
  const [rolePermissionSources, setRolePermissionSources] = useState<Record<string, string[]>>({});
  const [permissionLoading, setPermissionLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  // 使用 ref 缓存 allPermissions，避免循环依赖
  const allPermissionsRef = useRef<any[]>([]);

  const fetchAdminPermissionsAndAll = useCallback(async (adminId: string) => {
    if (!adminId) {
      return;
    }

    try {
      setPermissionLoading(true);
      console.log("开始获取管理员权限，adminId:", adminId);

      // 1. 获取管理员所属的所有角色及其权限
      const adminRolesResponse = await getAdminRoles(adminId);
      console.log("管理员角色响应:", adminRolesResponse);

      // 2. 获取管理员的额外权限
      const extraPermissionsResponse = await getAdminExtraPermissions(adminId);
      console.log("管理员额外权限响应:", extraPermissionsResponse);

      // 3. 获取所有可用权限（如果还没有缓存）
      let currentAllPermissions = allPermissionsRef.current;
      if (currentAllPermissions.length === 0) {
        console.log("获取所有操作列表...");
        const allPermissionsResponse = await getAllOperations();
        console.log("所有操作响应:", allPermissionsResponse);
        if (Array.isArray(allPermissionsResponse)) {
          currentAllPermissions = allPermissionsResponse;
          setAllPermissions(currentAllPermissions);
          allPermissionsRef.current = currentAllPermissions;
        }
      }

      // 处理角色权限数据
      let rolePermissionList: any[] = [];
      let rolePermissionMap: Record<string, string[]> = {}; // 权限ID -> 角色名称列表

      // 修复：API返回的数据结构是 { success: true, data: [...] }
      let rolesData: any[] = [];
      if (Array.isArray(adminRolesResponse?.data?.data)) {
        rolesData = adminRolesResponse.data.data;
      } else if (Array.isArray(adminRolesResponse?.data)) {
        rolesData = adminRolesResponse.data;
      }

      if (rolesData.length > 0) {
        console.log("角色数据:", rolesData);

        // 收集所有角色的权限
        rolesData.forEach((role: any) => {
          const roleName = role.name || role.role_name;
          const permissions = role.permissions || [];

          permissions.forEach((permission: any) => {
            const permId = permission.id?.toString() || permission.operation_id?.toString();
            if (permId) {
              // 添加到权限列表
              if (!rolePermissionList.find((p) => p.id === permId)) {
                rolePermissionList.push(permission);
              }

              // 记录权限来源（哪些角色提供了这个权限）
              if (!rolePermissionMap[permId]) {
                rolePermissionMap[permId] = [];
              }
              if (!rolePermissionMap[permId].includes(roleName)) {
                rolePermissionMap[permId].push(roleName);
              }
            }
          });
        });
      }

      // 处理管理员额外权限数据
      let extraPermissionList: any[] = [];
      if (extraPermissionsResponse?.data?.data && Array.isArray(extraPermissionsResponse.data.data)) {
        extraPermissionList = extraPermissionsResponse.data.data;
      } else if (Array.isArray(extraPermissionsResponse?.data)) {
        extraPermissionList = extraPermissionsResponse.data;
      }

      // 创建权限ID集合
      const rolePermissionIds = rolePermissionList.map((p) => p.id?.toString() || p.operation_id?.toString());
      const extraPermissionIds = extraPermissionList.map((p) => p.id?.toString() || p.operation_id?.toString());
      const allAssignedIds = new Set([...rolePermissionIds, ...extraPermissionIds]);

      console.log("🔍 角色权限列表原始数据:", rolePermissionList);
      console.log("🔍 角色权限ID集合:", rolePermissionIds);
      console.log("🔍 管理员额外权限ID集合:", extraPermissionIds);
      console.log("🔍 所有已分配权限ID集合:", Array.from(allAssignedIds));
      console.log("🔍 权限来源映射:", rolePermissionMap);

      setRolePermissions(rolePermissionIds);
      setExtraPermissions(extraPermissionIds);
      setRolePermissionSources(rolePermissionMap);

      // 构建完整的权限树形结构
      const permissionData: PermissionTransferItem[] = [];

      // 解析权限代码
      const parsePermissionCode = (code: string) => {
        if (!code) return { service: "", page: "", action: "" };
        const parts = code.split(":");
        if (parts.length === 3) {
          return { service: parts[0], page: parts[1], action: parts[2] };
        } else if (parts.length === 2) {
          return { service: parts[0], page: "通用", action: parts[1] };
        }
        return { service: parts[0] || "", page: "通用", action: "" };
      };

      // 按微服务分组
      const permissionsByService = currentAllPermissions.reduce(
        (acc: Record<string, any[]>, permission: any) => {
          const serviceName = permission.serviceName || permission.service_name || "未分类服务";
          if (!acc[serviceName]) {
            acc[serviceName] = [];
          }
          acc[serviceName].push(permission);
          return acc;
        },
        {} as Record<string, any[]>
      );

      // 为每个微服务创建三层树形结构
      Object.entries(permissionsByService).forEach(([serviceName, permissions]: [string, any[]]) => {
        // 第一层：微服务节点
        const serviceNode: PermissionTransferItem = {
          id: `service_${serviceName}`,
          key: `service_${serviceName}`,
          title: `${serviceName}服务`,
          description: `${serviceName}服务的所有权限`,
          type: "MODULE",
          children: [],
          permissionData: {
            serviceName,
            type: "MODULE",
          },
        };

        // 按页面分组
        const permissionsByPage: Record<string, any[]> = {};
        permissions.forEach((permission: any) => {
          const parsed = parsePermissionCode(permission.code);
          const pageName = parsed.page || "通用";
          if (!permissionsByPage[pageName]) {
            permissionsByPage[pageName] = [];
          }
          permissionsByPage[pageName].push(permission);
        });

        // 为每个页面创建第二层节点
        Object.entries(permissionsByPage).forEach(([pageName, pagePermissions]: [string, any[]]) => {
          const pageNode: PermissionTransferItem = {
            id: `page_${serviceName}_${pageName}`,
            key: `page_${serviceName}_${pageName}`,
            title: `${pageName}`,
            description: `${pageName}页面的权限`,
            type: "MODULE",
            children: [],
            permissionData: {
              serviceName,
              page: pageName,
              type: "MODULE",
            },
          };

          // 为每个权限创建第三层操作节点
          pagePermissions.forEach((permission: any) => {
            const permissionId = permission.id?.toString() || permission._id?.toString();
            if (permissionId) {
              // 判断是否被分配（来自角色 或 额外权限）
              const isAssigned = allAssignedIds.has(permissionId);
              // 判断是否来自角色（这些权限将被禁用）
              const isFromRole = rolePermissionIds.includes(permissionId);
              const parsed = parsePermissionCode(permission.code);

              const permissionNode: PermissionTransferItem = {
                id: permissionId,
                key: permissionId,
                title: permission.name || parsed.action || `权限(${permissionId})`,
                description: permission.description || permission.code || "",
                code: permission.code || "",
                type: "FUNCTION",
                isAssigned, // 是否被分配（来自角色或额外权限）
                isFromRole, // 是否来自角色（用于UI区分）
                permissionData: {
                  ...permission,
                  id: permissionId,
                  name: permission.name || "未命名",
                  code: permission.code || "",
                  type: "FUNCTION",
                  serviceName: permission.serviceName || permission.service_name,
                },
              };
              pageNode.children!.push(permissionNode);
            }
          });

          // 只有当页面节点有子权限时才添加
          if (pageNode.children!.length > 0) {
            serviceNode.children!.push(pageNode);
          }
        });

        // 只有当服务节点有子页面时才添加
        if (serviceNode.children!.length > 0) {
          permissionData.push(serviceNode);
        }
      });

      setAdminPermissions(permissionData);

      // 调试：检查生成的权限树中有多少个isAssigned=true的节点
      const countAssigned = (perms: PermissionTransferItem[]): number => {
        let count = 0;
        perms.forEach((p) => {
          if (p.isAssigned && p.type !== "MODULE") count++;
          if (p.children) count += countAssigned(p.children);
        });
        return count;
      };
      const assignedCount = countAssigned(permissionData);
      console.log("🔍 权限树中isAssigned=true的节点数:", assignedCount);

      // 更新管理员权限数量
      if (updateAdminPermissionCount && adminId) {
        updateAdminPermissionCount(adminId, assignedCount);
        console.log(`🔄 更新管理员 ${adminId} 的权限数量为: ${assignedCount}`);
      }
    } catch (error) {
      console.error("获取管理员权限失败:", error);
      message.error(`获取管理员权限失败: ${error instanceof Error ? error.message : "未知错误"}`);
    } finally {
      setPermissionLoading(false);
    }
  }, []);

  const handleSaveAdminPermissions = async (adminId: string, selectedPermissions?: PermissionTransferItem[]) => {
    console.log("=== 开始保存管理员额外权限 ===");
    console.log("管理员ID:", adminId);
    console.log("传入的选中权限:", selectedPermissions);

    if (!adminId) {
      message.warning("请先选择管理员");
      return;
    }

    try {
      setSaveLoading(true);

      // 如果传入了selectedPermissions，直接使用其ID
      let validPermissionIds: string[] = [];

      if (selectedPermissions && selectedPermissions.length > 0) {
        // 直接提取ID，不依赖isAssigned属性
        validPermissionIds = selectedPermissions.filter((p) => p.type === "FUNCTION").map((p) => p.id);
        console.log("直接从传入权限提取ID:", validPermissionIds);
      } else {
        // 从adminPermissions中提取额外权限（排除来自角色的权限）
        const extractExtraPermissionIds = (permissions: PermissionTransferItem[]): string[] => {
          const selectedIds: string[] = [];

          permissions.forEach((permission) => {
            // 只保存额外权限，不包括来自角色的权限
            if (permission.isAssigned && permission.type !== "MODULE" && !permission.isFromRole) {
              selectedIds.push(permission.id);
              console.log("管理员额外权限:", permission.title, permission.id);
            }

            if (permission.children) {
              selectedIds.push(...extractExtraPermissionIds(permission.children));
            }
          });

          return selectedIds;
        };

        validPermissionIds = extractExtraPermissionIds(adminPermissions);
        console.log("从adminPermissions提取额外权限ID:", validPermissionIds);
      }

      console.log("最终要保存的管理员额外权限ID列表:", validPermissionIds);

      // 调用API保存管理员额外权限
      console.log("调用API保存管理员额外权限...");
      const response = await saveAdminExtraPermissions(adminId, validPermissionIds);
      console.log("API响应:", response);

      if (response.data.success) {
        message.success("管理员额外权限保存成功");
        console.log("✅ 管理员额外权限保存成功");
      } else {
        message.error(response.data.message || "管理员额外权限保存失败");
      }
    } catch (error) {
      console.error("保存管理员额外权限失败:", error);
      message.error("保存管理员额外权限失败");
    } finally {
      setSaveLoading(false);
    }
  };

  return {
    adminPermissions,
    allPermissions,
    rolePermissions,
    extraPermissions,
    rolePermissionSources,
    permissionLoading,
    saveLoading,
    fetchAdminPermissionsAndAll,
    handleSaveAdminPermissions,
    updateAdminPermissionCount,
    setAdminPermissions,
  };
};
